import moment from 'moment';
import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCarBurst, faBan, faRotateRight, faPause, faEdit, faExternalLinkAlt, faCalendarAlt, faStickyNote, faClock,
} from '@fortawesome/free-solid-svg-icons';
import { after_sale_purchase_statues, accident_statuses } from '../../constants';
import { afterSaleCardsV2Css } from '../../../screens/CompletedQuotes/CompletedV2.styles';

const AfterSaleCardsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-bottom: 2rem;

  .after-sale-card {
    background: #ffffff;
    border-radius: 20px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05), 0 10px 40px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(0, 0, 0, 0.05);
    overflow: hidden;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05), 0 20px 60px rgba(0, 0, 0, 0.12);
      transform: translateY(-2px);
    }
  }

  .card-header-modern {
    padding: 1.75rem 2rem;
    background: linear-gradient(135deg, #1f2937 0%, #111827 100%);
    border: none;
    display: flex;
    align-items: center;
    gap: 1rem;
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      width: 200px;
      height: 200px;
      background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
      border-radius: 50%;
      transform: translate(30%, -30%);
    }

    &.header-accidents {
      background: linear-gradient(135deg, #dc2626 0%, #991b1b 100%);
      padding: 1rem 1.25rem;
    }

    &.header-cancellation {
      background: linear-gradient(135deg, #ea580c 0%, #c2410c 100%);
      padding: 1rem 1.25rem;
    }

    &.header-replacement {
      background: linear-gradient(135deg, #0891b2 0%, #0e7490 100%);
      padding: 1rem 1.25rem;
    }

    &.header-suspension {
      background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
      padding: 1rem 1.25rem;
    }

    &.header-modifications {
      background: linear-gradient(135deg, #059669 0%, #047857 100%);
      padding: 1rem 1.25rem;
    }

    .header-icon {
      width: 48px;
      height: 48px;
      background: rgba(255, 255, 255, 0.25);
      backdrop-filter: blur(10px);
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #ffffff;
      font-size: 1.25rem;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      position: relative;
      z-index: 1;
    }

    .header-title {
      color: #ffffff;
      font-size: 1.375rem;
      font-weight: 700;
      margin: 0;
      letter-spacing: -0.025em;
      position: relative;
      z-index: 1;
    }

    .header-count {
      margin-left: auto;
      background: rgba(255, 255, 255, 0.25);
      backdrop-filter: blur(10px);
      color: #ffffff;
      padding: 0.5rem 1rem;
      border-radius: 10px;
      font-size: 0.875rem;
      font-weight: 600;
      position: relative;
      z-index: 1;
    }
  }

  .table-container {
    overflow-x: auto;
    padding: 0;
  }

  .modern-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    margin: 0;

    thead {
      background: linear-gradient(to bottom, #f9fafb 0%, #f3f4f6 100%);
      
      th {
        padding: 1.25rem 1.5rem;
        text-align: left;
        font-size: 0.75rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: #6b7280;
        border-bottom: 2px solid #e5e7eb;
        white-space: nowrap;

        &:first-child {
          padding-left: 2rem;
        }

        &:last-child {
          padding-right: 2rem;
        }
      }
    }

    tbody {
      tr {
        transition: all 0.2s ease;
        background: #ffffff;

        &:hover {
          background: linear-gradient(to right, #f9fafb 0%, #ffffff 100%);
          box-shadow: inset 4px 0 0 #00a651;
        }

        &:not(:last-child) {
          border-bottom: 1px solid #f3f4f6;
        }

        td {
          padding: 1.25rem 1.5rem;
          font-size: 0.9375rem;
          color: #374151;
          vertical-align: middle;

          &:first-child {
            padding-left: 2rem;
            font-weight: 600;
            color: #111827;
          }

          &:last-child {
            padding-right: 2rem;
          }
        }
      }
    }
  }

  .status-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.5rem 0.875rem;
    border-radius: 8px;
    font-size: 0.8125rem;
    font-weight: 600;
    letter-spacing: 0.025em;
    white-space: nowrap;
    transition: all 0.2s ease;

    &::before {
      content: '';
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: currentColor;
    }

    &.badge-primary {
      background: rgba(0, 166, 81, 0.1);
      color: #047857;
      border: 1px solid rgba(0, 166, 81, 0.2);
    }

    &.badge-warning {
      background: rgba(245, 158, 11, 0.1);
      color: #d97706;
      border: 1px solid rgba(245, 158, 11, 0.2);
    }

    &.badge-danger {
      background: rgba(239, 68, 68, 0.1);
      color: #dc2626;
      border: 1px solid rgba(239, 68, 68, 0.2);
    }
  }

  .table-cell-with-icon {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #6b7280;

    svg {
      color: #9ca3af;
      font-size: 0.875rem;
    }
  }

  .action-button {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.625rem 1.25rem;
    background: linear-gradient(135deg, #00a651 0%, #00873f 100%);
    color: #ffffff;
    border: none;
    border-radius: 10px;
    font-size: 0.875rem;
    font-weight: 600;
    text-decoration: none;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 2px 8px rgba(0, 166, 81, 0.2);

    &:hover {
      background: linear-gradient(135deg, #00873f 0%, #006b32 100%);
      box-shadow: 0 4px 12px rgba(0, 166, 81, 0.3);
      transform: translateY(-2px);
      color: #ffffff;
    }

    &:active {
      transform: translateY(0);
    }

    svg {
      font-size: 0.75rem;
    }
  }

  .note-cell {
    max-width: 300px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #6b7280;
  }

  @media (max-width: 992px) {
    .modern-table {
      font-size: 0.875rem;

      thead th {
        padding: 1rem 1.25rem;
        font-size: 0.6875rem;

        &:first-child {
          padding-left: 1.5rem;
        }

        &:last-child {
          padding-right: 1.5rem;
        }
      }

      tbody td {
        padding: 1rem 1.25rem;
        font-size: 0.875rem;

        &:first-child {
          padding-left: 1.5rem;
        }

        &:last-child {
          padding-right: 1.5rem;
        }
      }
    }

    .note-cell {
      max-width: 200px;
    }
  }

  @media (max-width: 768px) {
    gap: 1.25rem;

    .card-header-modern {
      padding: 1.25rem 1.5rem;
      flex-wrap: wrap;

      .header-icon {
        width: 40px;
        height: 40px;
        font-size: 1rem;
      }

      .header-title {
        font-size: 1.125rem;
      }

      .header-count {
        flex-basis: 100%;
        margin-left: 0;
        margin-top: 0.75rem;
        text-align: center;
      }
    }

    .modern-table {
      thead th {
        padding: 0.875rem 1rem;

        &:first-child {
          padding-left: 1.25rem;
        }

        &:last-child {
          padding-right: 1.25rem;
        }
      }

      tbody td {
        padding: 0.875rem 1rem;

        &:first-child {
          padding-left: 1.25rem;
        }

        &:last-child {
          padding-right: 1.25rem;
        }
      }
    }

    .note-cell {
      max-width: 150px;
    }

    .action-button {
      padding: 0.5rem 1rem;
      font-size: 0.8125rem;
    }
  }

  ${(p) => p.$v2 && afterSaleCardsV2Css}
`;

function AfterSaleInfoCards({ afterSaleData, v2 = false }) {
  const typeNames = {
    accidents: 'Sinistri',
    cancellation_requests: 'Cessazione',
    replacement_requests: 'Sostituzione',
    suspension_requests: 'Sospensione',
    modifications: 'Modifiche',
  };

  const typeIcons = {
    accidents: faCarBurst,
    cancellation_requests: faBan,
    replacement_requests: faRotateRight,
    suspension_requests: faPause,
    modifications: faEdit,
  };

  const typeHeaderClass = {
    accidents: 'header-accidents',
    cancellation_requests: 'header-cancellation',
    replacement_requests: 'header-replacement',
    suspension_requests: 'header-suspension',
    modifications: 'header-modifications',
  };

  const getStatusName = (statusId, type) => {
    const statusArray = type === 'accidents' ? accident_statuses : after_sale_purchase_statues;
    const status = statusArray.find((s) => s.id === Number(statusId));
    return status ? status.name : 'Sconosciuto';
  };

  const getBadgeColor = (statusId, type) => {
    const badgeClassname = 'badge-primary';
    if (type === 'accidents') {
      switch (statusId) {
        case 1:
          return badgeClassname;
        case 2:
          return 'badge-warning';
        case 3:
          return 'badge-warning';
        case 4:
          return 'badge-danger';
        default:
          return badgeClassname;
      }
    }

    if (type !== 'accidents') {
      switch (statusId) {
        case 1:
          return 'badge-warning';
        case 2:
          return 'badge-danger';
        case 3:
          return 'badge-warning';
        case 4:
          return 'badge-warning';
        case 5:
          return badgeClassname;
        default:
          return badgeClassname;
      }
    }

    return badgeClassname;
  };

  const getUrl = (type, id) => {
    if (type === 'accidents') {
      return `/after-sale/purchase/accident/${id}`;
    }
    if (type === 'cancellation_requests') {
      return `/after-sale/purchase/cancellation/${id}`;
    }
    if (type === 'replacement_requests') {
      return `/after-sale/purchase/replacement/${id}`;
    }
    if (type === 'suspension_requests') {
      return `/after-sale/purchase/suspension/${id}`;
    }
    if (type === 'modifications') {
      return `/after-sale/purchase/modification/${id}`;
    }
    return '';
  };

  const getGroupedData = () => {
    if (!afterSaleData) return {};

    const groups = {};
    const afterSaleTypes = ['accidents', 'cancellation_requests', 'replacement_requests', 'suspension_requests', 'modifications'];

    afterSaleTypes.forEach((type) => {
      const items = afterSaleData[type];
      if (items && Array.isArray(items) && items.length > 0) {
        try {
          groups[type] = Array.from(items).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        } catch (error) {
          groups[type] = items;
        }
      }
    });

    return groups;
  };

  const renderTableRow = (item, index, type) => {
    const statusName = getStatusName(item.status || item.status_id, type);
    const badgeColor = getBadgeColor(item.status || item.status_id, type);
    const url = getUrl(type, item.id);

    return (
      <tr key={index}>
        <td>
          #
          {item.id}
        </td>
        <td>
          <span className={`status-badge ${badgeColor}`}>
            {statusName}
          </span>
        </td>
        <td>
          <div className="table-cell-with-icon">
            <FontAwesomeIcon icon={faCalendarAlt} />
            {moment(item.created_at).format('DD/MM/YYYY')}
          </div>
        </td>
        <td>
          <div className="table-cell-with-icon note-cell" title={item.note || item.notes || '-'}>
            <FontAwesomeIcon icon={faStickyNote} />
            {item.note || item.notes || '-'}
          </div>
        </td>
        <td>
          <div className="table-cell-with-icon">
            <FontAwesomeIcon icon={faClock} />
            {item.updated_at ? moment(item.updated_at).format('DD/MM/YYYY HH:mm') : '-'}
          </div>
        </td>
        <td>
          <Link to={url} className="action-button">
            Apri
            <FontAwesomeIcon icon={faExternalLinkAlt} />
          </Link>
        </td>
      </tr>
    );
  };

  const renderTypeCard = (type, items) => (
    <div key={type} className="after-sale-card">
      <div className={`card-header-modern ${typeHeaderClass[type]}`}>
        <div className="header-icon">
          <FontAwesomeIcon icon={typeIcons[type]} />
        </div>
        <h3 className="header-title">
          {typeNames[type]}
        </h3>
        <div className="header-count">
          {items.length}
          {' '}
          {items.length === 1 ? 'elemento' : 'elementi'}
        </div>
      </div>
      <div className="table-container">
        <table className="modern-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Stato</th>
              <th>Data Creazione</th>
              <th>Note</th>
              <th>Ultimo Aggiornamento</th>
              <th>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => renderTableRow(item, index, type))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const groupedData = getGroupedData();

  if (!groupedData || Object.keys(groupedData).length === 0) {
    return null;
  }

  return (
    <AfterSaleCardsWrapper $v2={v2}>
      {Object.entries(groupedData).map(([type, items]) => renderTypeCard(type, items))}
    </AfterSaleCardsWrapper>
  );
}

export default AfterSaleInfoCards;
